import React, { useMemo, useState } from "react";

function normalizeText(value) {
  return String(value || "").trim();
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "general")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getCategoryTone(category) {
  const normalized = normalizeText(
    category
  ).toLowerCase();

  if (normalized === "billing") {
    return {
      color: "#92400e",
      background: "#fef3c7",
    };
  }

  if (normalized === "staffing") {
    return {
      color: "#1d4ed8",
      background: "#dbeafe",
    };
  }

  if (normalized === "compliance") {
    return {
      color: "#7c3aed",
      background: "#ede9fe",
    };
  }

  if (normalized === "urgent") {
    return {
      color: "#b91c1c",
      background: "#fee2e2",
    };
  }

  return {
    color: "#475569",
    background: "#f1f5f9",
  };
}

export default function Facility360Notes({
  facility,
  onChangeFacility,
  currentUser,
}) {
  const [noteText, setNoteText] = useState("");
  const [category, setCategory] =
    useState("general");
  const [pinned, setPinned] = useState(false);
  const [filter, setFilter] = useState("all");

  const notes = useMemo(() => {
    const rows = Array.isArray(facility?.clientNotes)
      ? facility.clientNotes
      : [];

    return [...rows].sort((a, b) => {
      if (a?.pinned === true && b?.pinned !== true) {
        return -1;
      }

      if (b?.pinned === true && a?.pinned !== true) {
        return 1;
      }

      const aDate = toDate(
        a?.createdAt || a?.updatedAt
      );

      const bDate = toDate(
        b?.createdAt || b?.updatedAt
      );

      return (
        (bDate?.getTime() || 0) -
        (aDate?.getTime() || 0)
      );
    });
  }, [facility]);

  const visibleNotes = useMemo(() => {
    if (filter === "all") return notes;

    if (filter === "pinned") {
      return notes.filter(
        (note) => note?.pinned === true
      );
    }

    return notes.filter(
      (note) =>
        normalizeText(note?.category).toLowerCase() ===
        filter
    );
  }, [notes, filter]);

  const authorName =
    currentUser?.displayName ||
    currentUser?.name ||
    currentUser?.email ||
    "Administrator";

  function addNote() {
    const text = normalizeText(noteText);

    if (!text) {
      window.alert("Enter a client note.");
      return;
    }

    const note = {
      id: `facility-note-${Date.now()}`,
      text,
      category,
      pinned,
      createdAt: new Date().toISOString(),
      createdBy:
        currentUser?.uid ||
        currentUser?.id ||
        "",
      createdByName: authorName,
    };

    onChangeFacility?.((previous) => ({
      ...previous,
      clientNotes: [
        ...(Array.isArray(previous?.clientNotes)
          ? previous.clientNotes
          : []),
        note,
      ],
    }));

    setNoteText("");
    setCategory("general");
    setPinned(false);
  }

  function togglePinned(noteId) {
    onChangeFacility?.((previous) => ({
      ...previous,
      clientNotes: (
        Array.isArray(previous?.clientNotes)
          ? previous.clientNotes
          : []
      ).map((note) =>
        note?.id === noteId
          ? {
              ...note,
              pinned: note?.pinned !== true,
              updatedAt: new Date().toISOString(),
            }
          : note
      ),
    }));
  }

  function updateNoteCategory(noteId, nextCategory) {
  onChangeFacility?.((previous) => ({
    ...previous,
    clientNotes: (
      Array.isArray(previous?.clientNotes)
        ? previous.clientNotes
        : []
    ).map((note) =>
      note?.id === noteId
        ? {
            ...note,
            category: nextCategory,
            updatedAt: new Date().toISOString(),
          }
        : note
    ),
  }));
}

  function removeNote(noteId) {
    const confirmed = window.confirm(
      "Remove this client note?"
    );

    if (!confirmed) return;

    onChangeFacility?.((previous) => ({
      ...previous,
      clientNotes: (
        Array.isArray(previous?.clientNotes)
          ? previous.clientNotes
          : []
      ).filter((note) => note?.id !== noteId),
    }));
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Client Notes
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Internal operational notes about the facility,
          client relationship, staffing, billing, and
          compliance.
        </div>
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #dbeafe",
          background: "#eff6ff",
        }}
      >
        <div
          style={{
            color: "#1e3a8a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Add Client Note
        </div>

        <textarea
          value={noteText}
          rows={4}
          placeholder="Enter an internal facility note..."
          onChange={(event) =>
            setNoteText(event.target.value)
          }
          style={{
            width: "100%",
            marginTop: 10,
            padding: 11,
            boxSizing: "border-box",
            resize: "vertical",
            borderRadius: 11,
            border: "1px solid #bfdbfe",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(0, 1fr) auto",
            gap: 10,
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <select
            value={category}
            onChange={(event) =>
              setCategory(event.target.value)
            }
            style={{
              minHeight: 38,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid #bfdbfe",
              background: "#ffffff",
              color: "#0f172a",
              fontSize: 12,
              fontWeight: 750,
            }}
          >
            <option value="general">General</option>
            <option value="staffing">Staffing</option>
            <option value="billing">Billing</option>
            <option value="compliance">
              Compliance
            </option>
            <option value="urgent">Urgent</option>
          </select>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              color: "#475569",
              fontSize: 11,
              fontWeight: 850,
            }}
          >
            <input
              type="checkbox"
              checked={pinned}
              onChange={(event) =>
                setPinned(event.target.checked)
              }
            />

            Pin note
          </label>
        </div>

        <button
          type="button"
          onClick={addNote}
          style={{
            minHeight: 38,
            marginTop: 11,
            padding: "8px 13px",
            borderRadius: 10,
            border: "1px solid #2563eb",
            background: "#2563eb",
            color: "#ffffff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          Add Note
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 7,
          overflowX: "auto",
          paddingBottom: 3,
        }}
      >
        {[
          ["all", `All (${notes.length})`],
          [
            "pinned",
            `Pinned (${
              notes.filter(
                (note) => note?.pinned === true
              ).length
            })`,
          ],
          ["staffing", "Staffing"],
          ["billing", "Billing"],
          ["compliance", "Compliance"],
          ["urgent", "Urgent"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            style={{
              minHeight: 34,
              padding: "6px 11px",
              borderRadius: 999,
              border:
                filter === key
                  ? "1px solid #2563eb"
                  : "1px solid #cbd5e1",
              background:
                filter === key
                  ? "#2563eb"
                  : "#ffffff",
              color:
                filter === key
                  ? "#ffffff"
                  : "#334155",
              cursor: "pointer",
              fontSize: 10,
              fontWeight: 900,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {visibleNotes.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontWeight: 750,
          }}
        >
          No client notes match this filter.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {visibleNotes.map((note, index) => {
            const tone = getCategoryTone(
              note?.category
            );

            return (
              <div
                key={
                  note?.id ||
                  `facility-note-${index}`
                }
                style={{
                  padding: 14,
                  borderRadius: 14,
                  border:
                    note?.pinned === true
                      ? "1px solid #93c5fd"
                      : "1px solid #e2e8f0",
                  background:
                    note?.pinned === true
                      ? "#f8fbff"
                      : "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        padding: "5px 8px",
                        borderRadius: 999,
                        color: tone.color,
                        background: tone.background,
                        fontSize: 9,
                        fontWeight: 950,
                        textTransform: "uppercase",
                      }}
                    >
                      {titleCase(
                        note?.category || "general"
                      )}
                    </span>

                    {note?.pinned === true ? (
                      <span
                        style={{
                          padding: "5px 8px",
                          borderRadius: 999,
                          color: "#1d4ed8",
                          background: "#dbeafe",
                          fontSize: 9,
                          fontWeight: 950,
                        }}
                      >
                        PINNED
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 10,
                      fontWeight: 750,
                      textAlign: "right",
                    }}
                  >
                    {formatDateTime(
                      note?.createdAt ||
                        note?.updatedAt
                    )}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 10,
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {String(note?.text || "")}
                </div>

                <div
                  style={{
                    marginTop: 9,
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 750,
                  }}
                >
                  Added by{" "}
                  {String(
                    note?.createdByName ||
                      "Administrator"
                  )}
                </div>

                    <label
                        style={{
                            display: "grid",
                            gap: 5,
                            marginTop: 10,
                        }}
                    >
                        <span
                            style={{
                                color: "#64748b",
                                fontSize: 10,
                                fontWeight: 850,
                            }}
                        >
                            Note Category
                        </span>

                        <select
                            value={note?.category || "general"}
                            onChange={(event) =>
                                updateNoteCategory(
                                    note?.id,
                                    event.target.value
                                )
                            }
                            style={{
                                width: "100%",
                                minHeight: 36,
                                padding: "7px 9px",
                                borderRadius: 9,
                                border: "1px solid #cbd5e1",
                                background: "#ffffff",
                                color: "#0f172a",
                                fontSize: 11,
                                fontWeight: 750,
                            }}
                        >
                            <option value="general">General</option>
                            <option value="staffing">Staffing</option>
                            <option value="billing">Billing</option>
                            <option value="compliance">
                                Compliance
                            </option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </label>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 11,
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      togglePinned(note?.id)
                    }
                    style={{
                      padding: "7px 10px",
                      borderRadius: 9,
                      border: "1px solid #bfdbfe",
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: 900,
                    }}
                  >
                    {note?.pinned === true
                      ? "Unpin"
                      : "Pin"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      removeNote(note?.id)
                    }
                    style={{
                      padding: "7px 10px",
                      borderRadius: 9,
                      border: "1px solid #fecaca",
                      background: "#ffffff",
                      color: "#b91c1c",
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: 900,
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}